/**
 * Custom Header Component
 * Handles sticky behavior, mobile menu, and 3-level sidebar menu interactions
 */
class HeaderCustom extends HTMLElement {
  constructor() {
    super();
    this.mobileMenuToggle = this.querySelector('[data-mobile-menu-toggle]');
    this.drawer = this.querySelector('.header-custom-drawer');
    this.isDrawerOpen = false;
  }

  connectedCallback() {
    this.setupSidebarMenus();
    this.setupMobileMenu();
    this.setupAccessibility();
  }

  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  /**
   * Setup 3-level sidebar menu interactions
   * When hovering over a level-2 item, show its corresponding level-3 panel
   */
  setupSidebarMenus() {
    const sidebarMenus = this.querySelectorAll('.nav-submenu--sidebar');

    sidebarMenus.forEach(menu => {
      const level2Items = menu.querySelectorAll('.nav-submenu__level2 li');
      const level3Panels = menu.querySelectorAll('.nav-submenu__panel');

      level2Items.forEach(item => {
        // Mouse enter - activate panel
        item.addEventListener('mouseenter', () => {
          this.activateLevel3Panel(menu, item, level2Items, level3Panels);
        });

        // Keyboard focus
        const link = item.querySelector('a');
        if (link) {
          link.addEventListener('focus', () => {
            this.activateLevel3Panel(menu, item, level2Items, level3Panels);
          });
        }
      });

      // Activate first item by default when menu opens
      const parentItem = menu.closest('.nav-menu__item');
      if (parentItem) {
        parentItem.addEventListener('mouseenter', () => {
          const firstItem = menu.querySelector('.nav-submenu__level2 li');
          if (firstItem && !menu.querySelector('.nav-submenu__level2 li.active')) {
            firstItem.dispatchEvent(new Event('mouseenter'));
          }
        });
      }
    });
  }

  /**
   * Activate a level-3 panel
   */
  activateLevel3Panel(menu, item, level2Items, level3Panels) {
    const target = item.dataset.target;
    if (!target) return;

    // Deactivate all
    level2Items.forEach(i => i.classList.remove('active'));
    level3Panels.forEach(p => p.classList.remove('active'));

    // Activate matching
    item.classList.add('active');
    const panel = menu.querySelector(`[data-panel="${target}"]`);
    if (panel) {
      panel.classList.add('active');
    }
  }

  /**
   * Setup mobile menu drawer
   */
  setupMobileMenu() {
    if (!this.mobileMenuToggle || !this.drawer) return;

    const backdrop = this.drawer.querySelector('.header-custom-drawer__backdrop');
    const closeBtn = this.drawer.querySelector('.header-custom-drawer__close');

    // Toggle button
    this.mobileMenuToggle.addEventListener('click', () => {
      this.toggleDrawer();
    });

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeDrawer();
      });
    }

    // Backdrop click
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        this.closeDrawer();
      });
    }

    // Escape key
    this.handleEscapeKey = (e) => {
      if (e.key === 'Escape' && this.isDrawerOpen) {
        this.closeDrawer();
      }
    };
    document.addEventListener('keydown', this.handleEscapeKey);

    // Setup accordion items in drawer
    this.setupDrawerAccordion();
  }

  /**
   * Toggle mobile drawer
   */
  toggleDrawer() {
    if (this.isDrawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  /**
   * Open mobile drawer
   */
  openDrawer() {
    if (!this.drawer) return;

    this.isDrawerOpen = true;
    this.drawer.classList.add('is-open');
    this.mobileMenuToggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    const firstFocusable = this.drawer.querySelector('button, a');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  }

  /**
   * Close mobile drawer
   */
  closeDrawer() {
    if (!this.drawer) return;

    this.isDrawerOpen = false;
    this.drawer.classList.remove('is-open');
    this.mobileMenuToggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    // Return focus to toggle
    this.mobileMenuToggle?.focus();
  }

  /**
   * Setup accordion behavior in mobile drawer
   */
  setupDrawerAccordion() {
    const menuItems = this.drawer?.querySelectorAll('.drawer-menu__item');
    if (!menuItems) return;

    menuItems.forEach(item => {
      const toggle = item.querySelector('.drawer-menu__toggle');
      if (!toggle) return;

      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        item.classList.toggle('is-open');
      });
    });
  }

  /**
   * Setup keyboard accessibility for desktop menu
   */
  setupAccessibility() {
    const menuItems = this.querySelectorAll('.nav-menu__item');

    menuItems.forEach(item => {
      const link = item.querySelector('.nav-menu__link');
      const submenu = item.querySelector('.nav-submenu');

      if (!link || !submenu) return;

      // Enter key opens submenu
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && link.getAttribute('aria-haspopup') === 'true') {
          e.preventDefault();
          const firstSubmenuLink = submenu.querySelector('a');
          if (firstSubmenuLink) {
            firstSubmenuLink.focus();
          }
        }
      });

      // Tab trap within submenu
      submenu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          link.focus();
        }
      });
    });
  }
}

// Define custom element
if (!customElements.get('header-custom')) {
  customElements.define('header-custom', HeaderCustom);
}
